import { v4 as uuidv4 } from 'uuid';

import logger from '$lib/logger';
import { selectedRoaster } from '$lib/PersistedState.svelte';
import { formatTime } from '$lib/RLogUtils';
import { globalState, newRoastEvents } from '$lib/State.svelte';
import type { NewRoastEvent } from '$lib/types/RoastLog';
import { resetHeatingRate } from './heatingRate';

/**
 * Reset the raw (unconverted) temperature recording data.
 */
const resetRecordingState = () => {
  globalState.reset();
  newRoastEvents.current = [];
  globalState.isRecording = true;
};

export const addEvent = ({ time, temperature, description, isFirstCrack }: NewRoastEvent) => {
  const newEvent: NewRoastEvent = {
    id: uuidv4(),
    time,
    formattedTime: formatTime(time),
    // Here, note that the temperature is mostly useless except for the display. When uploading to
    // through the API, the time is what is used to lookup the temperature on the website. As such,
    // the temperature can be in the user's preferred temperature scale. If it's in F, that is
    // uploaded through the API, but isn't used to display anything directly.
    temperature,
    isFirstCrack,
    description
  };
  newRoastEvents.current = [newEvent, ...newRoastEvents.current];
};

export const deleteEvent = (event: NewRoastEvent) => {
  logger.info(`Deleting event: ${event.description}`);
  newRoastEvents.current = newRoastEvents.current.filter((e) => e.id !== event.id);
  if (event.isFirstCrack) {
    globalState.resetFirstCrack();
  }
};

export const startRecording = (): void => {
  // Do nothing if we're already recording. Safety check.
  if (globalState.isRecording) {
    logger.warn('Trying to record when already recording');
    return;
  }

  // Reset everything on startup
  resetRecordingState();
  resetHeatingRate();

  // Set the tc states on the sensortSnapshot state, which is responsible for doing all of the hard
  // work of temperature transformation and state management of all the data.
  globalState.tcStateMap = [
    true,
    selectedRoaster.tcSettings.current[1].active,
    selectedRoaster.tcSettings.current[2].active,
    selectedRoaster.tcSettings.current[3].active
  ];

  logger.info('Preparing to start recording', { tcStateMap: globalState.tcStateMap });
  globalState.startRecording(Date.now());
};

export const stopRecording = () => {
  globalState.stopRecording();
  resetHeatingRate();
};
